import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import axios from "axios";
import { OperaContext } from "../../../context/OperaContext";
import { toast } from "react-toastify";
import VenueListItem from "./venue-list-item";
import ImageModal from "../../../components/ImagePreviewModal";
import useGoogleDriveImagePicker from "../../../components/useGoogleDriveImagePicker";
import Collapsible from "../../../components/Collapsible";
import { Editor } from "@tinymce/tinymce-react";
import RichTextEditor from "../../../components/RichTextEditor";
const initialFormData = {
  name: "",
  cityNew: "",
  streetAddress: "",
  postalCode: "",
  country: "",
  description: "",
  shortDescription: "",
  includedInPrice: "",
  optionalExtras: "",
  coverImage: null,
  operaImage: null,
  seatingPlan: [],
  seatingPlanImage: null,
  seatingPlanDescription: "",
  defaultHotelRoom: { hotelId: "", roomType: "" },
};

function VenueTab({ setActiveTab }) {
  const {
    venues = [],
    setVenues = () => { },
    fetchAdminData,
    setPrePopulateEventData,
    venueFormData,
    setVenueFormData,
    venueFormDataEdit,
    setVenueFormDataEdit,
    dataFetched,
  } = useContext(OperaContext);
  const [formData, setFormData] = useState(
    venueFormDataEdit || venueFormData || initialFormData
  );
  const [hiddenVenues, setHiddenVenues] = useState([]);
  const [showHiddenVenues, setShowHiddenVenues] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(venueFormDataEdit ? true : false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCity, setSelectedCity] = useState("all");
  const [hotels, setHotels] = useState([]);
  const [newCities, setCities] = useState([]);
  const venueFormRef = useRef(null);
  const formContainerRef = useRef(null);
  const coverImageRef = useRef(null);
  const operaImageRef = useRef(null);
  const seatingPlanImageRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [isSectionOpen, setIsSectionOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const openModal = (image) => {
    setModalImage(image);
    setModalOpen(true);
  };

  // Filter venues by search term and city
  const filteredVisibleVenues = venues
    .filter((venue) => {
      const matchesSearch =
        venue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        venue.cityNew?.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCity = selectedCity === "all" || venue.cityNew?.name === selectedCity;
      return matchesSearch && matchesCity;
    })
    .sort((a, b) => {
      if (a.cityNew?.name !== b.cityNew?.name) return a.cityNew?.name.localeCompare(b.cityNew?.name);
      return a.name.localeCompare(b.name);
    });

  const filteredHotels = formData.cityNew
    ? hotels.filter(
      (hotel) =>
        hotel && hotel.cityNew?._id === formData.cityNew
    )
    : hotels;

  const filteredHiddenVenues = hiddenVenues
    .filter((venue) => {
      const matchesSearch =
        venue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        venue.cityNew?.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCity = selectedCity === "all" || venue.cityNew?.name === selectedCity;
      return matchesSearch && matchesCity;
    })
    .sort((a, b) => {
      if (a.cityNew?.name !== b.cityNew?.name) return a.cityNew?.name.localeCompare(b.cityNew?.name);
      return a.name.localeCompare(b.cityNew?.name);
    });

  const handleVenueChange = (e) => {
    const { name, value } = e.target;

    // Handle hotel and room type selection
    if (name === "defaultHotelId") {
      setFormData((prev) => ({
        ...prev,
        defaultHotelRoom: {
          ...prev.defaultHotelRoom,
          hotelId: value,
          roomType: "", // Reset room type when hotel changes
        },
      }));

      if (!isEditing) {
        const updatedFormData = {
          ...formData,
          defaultHotelRoom: {
            ...formData.defaultHotelRoom,
            hotelId: value,
            roomType: "",
          },
        };

        setVenueFormData(updatedFormData);
      }
      if (isEditing) {
        const updatedFormData = {
          ...formData,
          defaultHotelRoom: {
            ...formData.defaultHotelRoom,
            hotelId: value,
            roomType: "",
          },
        };

        setVenueFormDataEdit(updatedFormData);
      }
    } else if (name === "defaultRoomType") {
      setFormData((prev) => ({
        ...prev,
        defaultHotelRoom: {
          ...prev.defaultHotelRoom,
          roomType: value,
        },
      }));

      // Save to context if not editing
      if (!isEditing) {
        const updatedFormData = {
          ...formData,
          defaultHotelRoom: {
            ...formData.defaultHotelRoom,
            roomType: value,
          },
        };

        setVenueFormData(updatedFormData);
      }
      if (isEditing) {
        const updatedFormData = {
          ...formData,
          defaultHotelRoom: {
            ...formData.defaultHotelRoom,
            roomType: value,
          },
        };

        setVenueFormDataEdit(updatedFormData);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      const updatedFormData = { ...formData, [name]: value };
      isEditing
        ? setVenueFormDataEdit(updatedFormData)
        : setVenueFormData(updatedFormData);
    }
  };

  const handleDescriptionChange = (field, value) => {
    // update local form state
    setFormData((prev) => ({ ...prev, [field]: value }));

    // update context while preserving id
    if (isEditing) {
      setVenueFormDataEdit((prev) => ({ ...(prev || {}), [field]: value }));
    } else {
      setVenueFormData((prev) => ({ ...(prev || {}), [field]: value }));
    }
  };

  const handleSeatingPlanChange = (index, field, value) => {
    const updatedSeatingPlan = [...formData.seatingPlan];
    const numericValue = field === "markup" ? parseFloat(value) || 0 : value;
    updatedSeatingPlan[index] = {
      ...updatedSeatingPlan[index],
      [field]: numericValue,
    };
    setFormData((prev) => ({ ...prev, seatingPlan: updatedSeatingPlan }));
    isEditing
      ? setVenueFormDataEdit((prev) => ({
        ...prev,
        seatingPlan: updatedSeatingPlan,
      }))
      : setVenueFormData((prev) => ({
        ...prev,
        seatingPlan: updatedSeatingPlan,
      }));
  };

  const addSeatingCategory = () => {
    setFormData((prev) => ({
      ...prev,
      seatingPlan: [...(prev?.seatingPlan || []), { category: "", markup: 0 }],
    }));

    if (isEditing && setVenueFormDataEdit) {
      setVenueFormDataEdit((prev) => ({
        ...(prev || {}),
        seatingPlan: [
          ...(prev?.seatingPlan || []),
          { category: "", markup: 0 },
        ],
      }));
    } else if (!isEditing && setVenueFormData) {
      setVenueFormData((prev) => ({
        ...(prev || {}),
        seatingPlan: [
          ...(prev?.seatingPlan || []),
          { category: "", markup: 0 },
        ],
      }));
    }
  };

  const removeSeatingCategory = (index) => {
    const updatedSeatingPlan = [...formData.seatingPlan];
    updatedSeatingPlan.splice(index, 1);
    setFormData((prev) => ({ ...prev, seatingPlan: updatedSeatingPlan }));
    isEditing
      ? setVenueFormDataEdit((prev) => ({
        ...prev,
        seatingPlan: updatedSeatingPlan,
      }))
      : setVenueFormData((prev) => ({
        ...prev,
        seatingPlan: updatedSeatingPlan,
      }));
  };

  const handleVenueFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Please upload an image file (e.g., JPG, PNG)");
        setFormData((prev) => ({ ...prev, [name]: null }));
        isEditing
          ? setVenueFormDataEdit((prev) => ({ ...prev, [name]: null }))
          : setVenueFormData((prev) => ({ ...prev, [name]: null }));
        e.target.value = null;
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Image file size must be less than 5MB");
        setFormData((prev) => ({ ...prev, [name]: null }));
        isEditing
          ? setVenueFormDataEdit((prev) => ({ ...prev, [name]: null }))
          : setVenueFormData((prev) => ({ ...prev, [name]: null }));
        e.target.value = null;
        return;
      }
    }
    setError(null);
    setFormData((prev) => ({ ...prev, [name]: file }));
    if (isEditing) {
      setVenueFormDataEdit((prev) => ({ ...prev, [name]: file }));
    } else {
      setVenueFormData((prev) => ({ ...prev, [name]: file }));
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setVenueFormDataEdit(null);
    setIsEditing(false);
    setError(null);
    setShowPreview(false);
    // clear form data from context
    if (setVenueFormData) {
      setVenueFormData(null);
    }
    // Reset file inputs
    if (coverImageRef.current) coverImageRef.current.value = null;
    if (operaImageRef.current) operaImageRef.current.value = null;
    if (seatingPlanImageRef.current) seatingPlanImageRef.current.value = null;
    setIsSectionOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fetchVenueById = async (venueId) => {
    try {
      const response = await axios.get(`/api/venues/${venueId}`);
      // Ensure defaultHotelRoom structure exists
      if (!response.data.defaultHotelRoom) {
        response.data.defaultHotelRoom = { hotelId: "", roomType: "" };
      }
      return response.data;
    } catch (error) {
      setError("Failed to fetch venue details");
      return null;
    }
  };

  // Fetch hotels for the default hotel selection
  const fetchHotels = useCallback(async (retryCount = 0, maxRetries = 3) => {
    try {
      const response = await axios.get("/api/hotels", {
        params: {
          limit: 1000,
        },
      });
      const fetchedHotels = response.data.hotels;
      setHotels(fetchedHotels);
    } catch (err) {
      if (retryCount < maxRetries) {
        const delay = 3000 * Math.pow(2, retryCount);
        setTimeout(() => fetchHotels(retryCount + 1, maxRetries), delay);
      } else {
        setError("Failed to load hotels. Please try again later.");
      }
    }
  }, []);
  const fetchCities = useCallback(async () => {
    try {
      const response = await axios.get("/api/cities", { timeout: 15000 });
      const fetchedCities = response.data;
      setCities(fetchedCities);
    } catch (err) {
      setError("Failed to load cities. Please try again later.");
    }
  }, []);
  const fetchHiddenVenues = useCallback(
    async (retryCount = 0, maxRetries = 3) => {
      try {
        const res = await axios.get("/api/venues/hidden", { timeout: 15000 });
        setHiddenVenues(Array.isArray(res.data) ? res.data : []);
        setError(null);
      } catch (err) {
        if (retryCount < maxRetries) {
          const delay = 3000 * Math.pow(2, retryCount);
          setTimeout(
            () => fetchHiddenVenues(retryCount + 1, maxRetries),
            delay
          );
        } else {
          setError("Failed to load hidden venues.");
          setHiddenVenues([]);
        }
      }
    },
    []
  );

  const submitVenue = async (submitData, isEdit = false, id = null) => {
    try {
      const url = isEdit ? `/api/venues/${id}` : "/api/venues";
      const method = isEdit ? "put" : "post";
      const res = await axios({
        method,
        url,
        data: submitData,
        timeout: 15000,
        withCredentials: true,
      });
      if (res.status === 200) {
        toast.success(
          isEdit ? "Successfully updated venue!" : "Successfully added venue!"
        );
        setIsSectionOpen(false);
      }
      fetchAdminData();
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        `Failed to ${isEdit ? "update" : "add"} venue`;
      toast.error(errorMsg);

      throw new Error(String(errorMsg));
    }
  };

  const handleVenueSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.cityNew) {
      setError("Name and City are required");
      return;
    }

    // Seating plan validation is now more permissive
    // Empty categories are allowed since they're optional in the model
    // We only need to validate that the markup is a number if provided

    // Validate default hotel and room type if either is provided
    // Both fields must be provided together if one is provided
    if (
      (formData.defaultHotelRoom.hotelId &&
        !formData.defaultHotelRoom.roomType) ||
      (!formData.defaultHotelRoom.hotelId && formData.defaultHotelRoom.roomType)
    ) {
      setError("Both Default Hotel and Room Type must be selected together");
      return;
    }

    const dataToSubmit = new FormData();
    dataToSubmit.append("name", formData.name);
    dataToSubmit.append("streetAddress", formData.streetAddress);
    dataToSubmit.append("cityNew", formData.cityNew);
    dataToSubmit.append("postalCode", formData.postalCode);
    dataToSubmit.append("country", formData.country);
    dataToSubmit.append("description", formData.description);
    dataToSubmit.append("shortDescription", formData.shortDescription);
    dataToSubmit.append("includedInPrice", formData.includedInPrice);
    dataToSubmit.append("optionalExtras", formData.optionalExtras);
    dataToSubmit.append("seatingPlan", JSON.stringify(formData.seatingPlan));
    dataToSubmit.append(
      "seatingPlanDescription",
      formData.seatingPlanDescription || ""
    );

    // Add defaultHotelRoom data if provided
    if (
      formData.defaultHotelRoom.hotelId &&
      formData.defaultHotelRoom.roomType
    ) {
      dataToSubmit.append(
        "defaultHotelRoom",
        JSON.stringify(formData.defaultHotelRoom)
      );
    }

    if (formData.coverImage instanceof File)
      dataToSubmit.append("coverImage", formData.coverImage);
    if (formData.operaImage instanceof File)
      dataToSubmit.append("operaImage", formData.operaImage);
    if (formData.seatingPlanImage instanceof File)
      dataToSubmit.append("seatingPlanImage", formData.seatingPlanImage);

    try {
      setError(null);

      isEditing
        ? await submitVenue(dataToSubmit, true, venueFormDataEdit.id)
        : await submitVenue(dataToSubmit, false, null);
      resetForm();
    } catch (err) { }
  };

  const editVenue = async (venue) => {
    setShowPreview(false);
    setIsSectionOpen(true);
    const venueData = await fetchVenueById(venue._id);
    if (!venueData) return;

    // Reset file inputs
    if (coverImageRef.current) coverImageRef.current.value = "";
    if (operaImageRef.current) operaImageRef.current.value = "";
    if (seatingPlanImageRef.current) seatingPlanImageRef.current.value = "";

    // Ensure seatingPlan has at least one item
    const seatingPlan =
      venueData.seatingPlan && venueData.seatingPlan.length > 0
        ? venueData.seatingPlan
        : [{ category: "", markup: 0 }];

    // Ensure defaultHotelRoom exists
    const defaultHotelRoom = venueData.defaultHotelRoom || {
      hotelId: "",
      roomType: "",
    };

    setFormData({
      name: venueData.name || "",
      postalCode: venueData.postalCode || "",
      streetAddress: venueData.streetAddress || "",
      cityNew: venueData.cityNew?._id || "",
      country: venueData.country || "",
      description: venueData.description || "",
      shortDescription: venueData.shortDescription || "",
      includedInPrice: venueData.includedInPrice || "",
      optionalExtras: venueData.optionalExtras || "",
      coverImage: venueData.coverImage || "",
      operaImage: venueData.operaImage || "",
      seatingPlan,
      seatingPlanImage: venueData.seatingPlanImage || "",
      seatingPlanDescription: venueData.seatingPlanDescription || "",
      defaultHotelRoom,
    });
    setVenueFormDataEdit({
      id: venueData._id,
      name: venueData.name || "",
      postalCode: venueData.postalCode || "",
      streetAddress: venueData.streetAddress || "",
      cityNew: venueData.cityNew?._id || "",
      country: venueData.country || "",
      description: venueData.description || "",
      shortDescription: venueData.shortDescription || "",
      includedInPrice: venueData.includedInPrice || "",
      optionalExtras: venueData.optionalExtras || "",
      coverImage: venueData.coverImage || "",
      operaImage: venueData.operaImage || "",
      seatingPlan,
      seatingPlanImage: venueData.seatingPlanImage || "",
      seatingPlanDescription: venueData.seatingPlanDescription || "",
      defaultHotelRoom,
    });
    setIsEditing(true);
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAddEvent = (venue) => {
    setPrePopulateEventData({ venueId: venue._id, city: venue.cityNew });
    setActiveTab("events");
  };

  const deleteVenueHandler = async (venueId) => {
    if (!window.confirm("Are you sure you want to delete this venue?")) return;
    try {
      const response = await axios.delete(`/api/venues/${venueId}`, {
        timeout: 15000,
      });
      if (response.status === 200) {
        toast.success(response.message || "Venue deleted successfully!");
        resetForm();
        fetchAdminData();
      }
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to delete venue"
      );
    }
  };

  const hideVenue = async (venueId, retryCount = 0, maxRetries = 3) => {
    try {
      await axios.put(`/api/venues/${venueId}/hide`, {}, { timeout: 15000 });
      fetchAdminData();
      setError(null);
      if (venueFormDataEdit?.id === venueId) {
        resetForm();
      }
    } catch (err) {
      if (retryCount < maxRetries) {
        const delay = 3000 * Math.pow(2, retryCount);
        setTimeout(() => hideVenue(venueId, retryCount + 1, maxRetries), delay);
      } else {
        setError("Failed to hide venue");
      }
    }
  };

  const unhideVenue = async (venueId, retryCount = 0, maxRetries = 3) => {
    try {
      await axios.put(`/api/venues/${venueId}/unhide`, {}, { timeout: 15000 });
      fetchAdminData();
      if (showHiddenVenues) fetchHiddenVenues();
      setError(null);
    } catch (err) {
      if (retryCount < maxRetries) {
        const delay = 3000 * Math.pow(2, retryCount);
        setTimeout(
          () => unhideVenue(venueId, retryCount + 1, maxRetries),
          delay
        );
      } else {
        setError("Failed to unhide venue");
      }
    }
  };

  const publishVenue = async (venueId, retryCount = 0, maxRetries = 3) => {
    try {
      const response = await axios.put(
        `/api/venues/${venueId}/publish`,
        {},
        { timeout: 15000 }
      );
      setVenues((prev) =>
        prev.map((v) => (v._id === venueId ? { ...v, published: true } : v))
      );
      fetchAdminData();
      if (response.status === 200) {
        toast.success("Published venue successfully");
      } else if (response.data?.message) {
        toast.error(response.data?.message);
      }
    } catch (err) {
      if (retryCount < maxRetries) {
        const delay = 3000 * Math.pow(2, retryCount);
        setTimeout(
          () => publishVenue(venueId, retryCount + 1, maxRetries),
          delay
        );
      } else {
        toast.error("Failed to publish venue");
      }
    }
  };

  const unpublishVenue = async (venueId, retryCount = 0, maxRetries = 3) => {
    try {
      await axios.put(
        `/api/venues/${venueId}/unpublish`,
        {},
        { timeout: 15000 }
      );
      toast.success("Unpublished venue");
      setVenues((prev) =>
        prev.map((v) => (v._id === venueId ? { ...v, published: false } : v))
      );
      fetchAdminData();
      setError(null);
    } catch (err) {
      if (retryCount < maxRetries) {
        const delay = 3000 * Math.pow(2, retryCount);
        setTimeout(
          () => unpublishVenue(venueId, retryCount + 1, maxRetries),
          delay
        );
      } else {
        toast.error("Failed to unpublish venue");
      }
    }
  };

  useEffect(() => {
    if (venueFormDataEdit) {
      setFormData(venueFormDataEdit);
      setIsEditing(true);
      return;
    } else if (venueFormData) {
      setFormData(venueFormData);
    }
  }, [venueFormDataEdit, formData, venueFormData]);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!dataFetched) {
          await fetchAdminData();
        }
        fetchHiddenVenues();
        fetchHotels();
        fetchCities();
      } catch (err) { }
    };

    loadData();
  }, [fetchAdminData, dataFetched, fetchHiddenVenues, fetchCities, fetchHotels]);

  useEffect(() => {
    if (showHiddenVenues) fetchHiddenVenues();
    else setHiddenVenues([]);
  }, [showHiddenVenues, fetchHiddenVenues]);

  const handleOpenPicker = useGoogleDriveImagePicker({
    isEditing,
    setFormData: setVenueFormData,
    setFormDataEdit: setVenueFormDataEdit,
    setError,
  });
  return (
    <div className="max-w-[1280px] mx-auto p-4 md:p-8 bg-[#1a1a1a] rounded-lg text-white">
      <ImageModal
        open={modalOpen}
        image={modalImage}
        onClose={() => setModalOpen(false)}
      />
      <Collapsible
        title={isEditing ? "Edit Venue" : "Add Venue"}
        defaultOpen={true}
        isOpen={isSectionOpen}
        setIsOpen={setIsSectionOpen}
      >
        <div ref={formContainerRef}>
          {error && <p className="text-[#f44336] text-center mb-4">{error}</p>}
          <form
            onSubmit={handleVenueSubmit}
            ref={venueFormRef}
            className="flex flex-col gap-4 mb-8"
          >
            <div className="flex flex-col gap-1">
              <label className="text-white">Venue Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleVenueChange}
                required
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md"
              />
            </div>
            <div className="flex flex-col gap-1 mb-4">
              <label className="text-white">City *</label>
              <select
                name="cityNew"
                value={formData.cityNew}
                onChange={handleVenueChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md focus:border-[#d4a017]"
              >
                <option value="">Select City</option>
                {newCities?.map((cities) => (
                  <option key={cities._id} value={cities._id}>
                    {cities.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">Postal Code</label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleVenueChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Street Address</label>
              <input
                type="text"
                name="streetAddress"
                value={formData.streetAddress}
                onChange={handleVenueChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Country</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleVenueChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Short Description</label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleVenueChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md min-h-[100px] resize-y"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Description</label>
              <button
                type="button"
                className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors disabled:bg-[#e6c347] disabled:cursor-not-allowed"
                onClick={() => setShowPreview((prev) => !prev)}
              >
                {showPreview ? "Edit Text" : "Edit HTML"}
              </button>
              {showPreview && <textarea
                name="description"
                value={formData.description}
                onChange={handleVenueChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md min-h-[150px] resize-y"
              />}
              {!showPreview && <RichTextEditor
                value={formData.description}
                onChange={(content) =>
                  handleDescriptionChange("description", content)
                }
              />}
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">Included in Price</label>
              <textarea
                name="includedInPrice"
                value={formData.includedInPrice}
                onChange={handleVenueChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md min-h-[100px] resize-y"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Optional Extras</label>
              <textarea
                name="optionalExtras"
                value={formData.optionalExtras}
                onChange={handleVenueChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md min-h-[100px] resize-y"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-white">Seating Plan Categories</label>
              {formData.seatingPlan?.map((category, index) => (
                <div
                  key={index}
                  className="flex flex-col md:flex-row gap-2 items-start md:items-center border border-[#555] rounded-md p-3"
                >
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-white text-sm">Category Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Platea"
                      value={category.category}
                      onChange={(e) =>
                        handleSeatingPlanChange(
                          index,
                          "category",
                          e.target.value
                        )
                      }
                      className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
                    />
                  </div>
                  <div className="flex flex-col gap-1 md:w-[150px]">
                    <label className="text-white text-sm">Markup (€)</label>
                    <input
                      type="number"
                      placeholder="e.g., 100"
                      value={category.markup}
                      onChange={(e) =>
                        handleSeatingPlanChange(index, "markup", e.target.value)
                      }
                      className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
                    />
                  </div>
                  {formData.seatingPlan.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSeatingCategory(index)}
                      className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded-md transition-colors mt-auto"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addSeatingCategory}
                disabled={!formData?.name}
                className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors disabled:bg-[#e6c347] disabled:cursor-not-allowed"
              >
                Add Category
              </button>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Seating Plan Description</label>
              <button
                type="button"
                className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors disabled:bg-[#e6c347] disabled:cursor-not-allowed"
                onClick={() => setShowPreview((prev) => !prev)}
              >
                {showPreview ? "Edit Text" : "Edit HTML"}
              </button>
              {showPreview &&
                <textarea
                  name="seatingPlanDescription"
                  value={formData.seatingPlanDescription}
                  onChange={handleVenueChange}
                  className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa] min-h-[100px] resize-y"
                />}
              {!showPreview && <RichTextEditor
                value={formData.seatingPlanDescription}
                onChange={(content) =>
                  handleDescriptionChange("seatingPlanDescription", content)
                }
              />}
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">Cover Image</label>
              {formData.coverImage && (
                <div className="mb-2">
                  <img
                    src={
                      formData.coverImage instanceof File
                        ? URL.createObjectURL(formData.coverImage)
                        : formData.coverImage
                    }
                    alt="Current Cover"
                    className="max-h-20 w-[100px]"
                    onClick={() =>
                      openModal(
                        formData.coverImage instanceof File
                          ? URL.createObjectURL(formData.coverImage)
                          : formData.coverImage
                      )
                    }
                  />
                </div>
              )}
              <div className="flex gap-2">
                <div className="relative">
                  <input
                    type="file"
                    id="coverImage-file"
                    name="coverImage"
                    ref={coverImageRef}
                    onChange={handleVenueFileChange}
                    accept="image/*"
                    className="w-fit pl-10 pr-3 py-2 text-base bg-[#333] text-white border border-[#555] rounded-md"
                  />
                  <label
                    htmlFor="coverImage-file"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none"
                  >
                    <img
                      src="/images/attach.svg"
                      alt="Choose File"
                      className="w-4 h-4 filter invert"
                    />
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => handleOpenPicker("coverImage")}
                  className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
                >
                  <img
                    src="/images/drive.png"
                    alt="Google Drive"
                    className="w-4 h-4"
                  />
                  Google Drive
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">Opera Image</label>
              {formData.operaImage && (
                <div className="mb-2">
                  <img
                    src={
                      formData.operaImage instanceof File
                        ? URL.createObjectURL(formData.operaImage)
                        : formData.operaImage
                    }
                    alt="Current Cover"
                    className="max-h-20 w-[100px]"
                    onClick={() =>
                      openModal(
                        formData.operaImage instanceof File
                          ? URL.createObjectURL(formData.operaImage)
                          : formData.operaImage
                      )
                    }
                  />
                </div>
              )}
              <div className="flex gap-2">
                <div className="relative">
                  <input
                    type="file"
                    id="operaImage-file"
                    name="operaImage"
                    ref={operaImageRef}
                    onChange={handleVenueFileChange}
                    accept="image/*"
                    className="w-fit pl-10 pr-3 py-2 text-base bg-[#333] text-white border border-[#555] rounded-md"
                  />
                  <label
                    htmlFor="operaImage-file"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none"
                  >
                    <img
                      src="/images/attach.svg"
                      alt="Choose File"
                      className="w-4 h-4 filter invert"
                    />
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => handleOpenPicker("operaImage")}
                  className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
                >
                  <img
                    src="/images/drive.png"
                    alt="Google Drive"
                    className="w-4 h-4"
                  />
                  Google Drive
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">Seating Plan Image</label>
              {formData.seatingPlanImage && (
                <div className="mb-2">
                  <img
                    src={
                      formData.seatingPlanImage instanceof File
                        ? URL.createObjectURL(formData.seatingPlanImage)
                        : formData.seatingPlanImage
                    }
                    alt="Current Cover"
                    className="max-h-20 w-[100px]"
                    onClick={() =>
                      openModal(
                        formData.seatingPlanImage instanceof File
                          ? URL.createObjectURL(formData.seatingPlanImage)
                          : formData.seatingPlanImage
                      )
                    }
                  />
                </div>
              )}
              <div className="flex gap-2">
                <div className="relative">
                  <input
                    type="file"
                    id="seatingPlanImage-file"
                    name="seatingPlanImage"
                    ref={seatingPlanImageRef}
                    onChange={handleVenueFileChange}
                    accept="image/*"
                    className="w-fit pl-10 pr-3 py-2 text-base bg-[#333] text-white border border-[#555] rounded-md"
                  />
                  <label
                    htmlFor="seatingPlanImage-file"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none"
                  >
                    <img
                      src="/images/attach.svg"
                      alt="Choose File"
                      className="w-4 h-4 filter invert"
                    />
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => handleOpenPicker("seatingPlanImage")}
                  className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
                >
                  <img
                    src="/images/drive.png"
                    alt="Google Drive"
                    className="w-4 h-4"
                  />
                  Google Drive
                </button>
              </div>
            </div>
            {/* Default Hotel and Room Type Selection */}
            <div className="mb-4">
              <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal mb-2">
                Default Hotel & Room Type
              </h3>
              <p className="text-[#ccc] text-sm mb-4">
                The default hotel and room type will be used for calculating the
                base price of events at this venue.
              </p>

              <div className="flex flex-col gap-1 mb-4">
                <label className="text-white">Default Hotel</label>
                <select
                  name="defaultHotelId"
                  value={formData.defaultHotelRoom?.hotelId}
                  onChange={handleVenueChange}
                  className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md focus:border-[#d4a017]"
                >
                  <option value="">Select Default Hotel (Optional)</option>
                  {filteredHotels
                    .filter(
                      (hotel) => hotel && hotel._id && hotel.name && hotel.cityNew?.name
                    )
                    .map((hotel) => (
                      <option key={hotel._id} value={hotel._id}>
                        {hotel.name} - {hotel.cityNew?.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-white">Default Room Type</label>
                <select
                  name="defaultRoomType"
                  value={formData.defaultHotelRoom?.roomType}
                  onChange={handleVenueChange}
                  disabled={!formData.defaultHotelRoom?.hotelId}
                  className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md focus:border-[#d4a017]"
                >
                  <option value="">Select Room Type (Optional)</option>
                  {formData.defaultHotelRoom?.hotelId &&
                    hotels
                      .find((h) => h._id === formData.defaultHotelRoom.hotelId)
                      ?.roomTypes.map((roomType) => (
                        <option key={roomType.type} value={roomType.type}>
                          {roomType.type}
                        </option>
                      ))}
                </select>
              </div>
            </div>
            <button
              type="submit"
              className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors"
            >
              {isEditing ? "Update Venue" : "Add Venue"}
            </button>
            {isEditing && (
              <div className="flex flex-col md:flex-row gap-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="w-full md:w-auto px-4 py-2 bg-[#333] hover:bg-[#555] text-white rounded-md transition-colors"
                >
                  Cancel Edit
                </button>
                <button
                  type="button"
                  onClick={() =>
                    deleteVenueHandler(
                      venueFormDataEdit?.id,
                      venues.find((v) => v._id === venueFormDataEdit?.id)
                        ?.hidden
                    )
                  }
                  className="w-full md:w-auto px-4 py-2 bg-[#f44336] hover:bg-[#da190b] text-white rounded-md transition-colors"
                >
                  Delete Venue
                </button>
              </div>
            )}
          </form>
        </div>
      </Collapsible>

      <div className="mb-6 flex flex-col md:flex-row gap-3">
        <div className="flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search venues..."
            className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
          />
        </div>
        <div>
          <select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md"
          >
            <option key={'all'} value={'all'}>
              All Cities
            </option>
            {newCities.map((city) => (
              <>
                <option key={city?._id} value={city.name}>
                  {city === "all" ? "All Cities" : city.name}
                </option>
              </>
            ))}
          </select>
        </div>
      </div>

      <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mb-4">
        Existing Venues{" "}
        {filteredVisibleVenues.length > 0 &&
          `(${filteredVisibleVenues.length})`}
      </h3>

      <div className="max-h-[400px] overflow-y-auto mb-6">
        {filteredVisibleVenues.length === 0 ? (
          <p className="text-[#ccc] text-center">
            {searchTerm || selectedCity !== "all"
              ? "No venues match your search."
              : "No visible venues available."}
          </p>
        ) : (
          <ul className="list-none p-0">
            {filteredVisibleVenues.map((venue) => {
              return (
                <VenueListItem
                  key={venue._id}
                  venue={venue}
                  editVenue={editVenue}
                  handleAddEvent={handleAddEvent}
                  actionLabel="Hide"
                  actionHandler={hideVenue}
                  unpublishVenue={unpublishVenue}
                  publishVenue={publishVenue}
                />
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex justify-center md:justify-start mt-4">
        <label className="flex items-center gap-2 text-white">
          <input
            type="checkbox"
            checked={showHiddenVenues}
            onChange={(e) => setShowHiddenVenues(e.target.checked)}
            className="h-4 w-4 text-[#d4a017] bg-[#333] border-[#555] rounded"
          />
          Show Hidden Venues
        </label>
      </div>

      {showHiddenVenues && (
        <>
          <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mt-4 mb-4">
            Hidden Venues{" "}
            {filteredHiddenVenues.length > 0 &&
              `(${filteredHiddenVenues.length})`}
          </h3>
          <div className="max-h-[400px] overflow-y-auto">
            {filteredHiddenVenues.length === 0 ? (
              <p className="text-[#ccc] text-center">
                {searchTerm || selectedCity !== "all"
                  ? "No hidden venues match your search."
                  : "No hidden venues found."}
              </p>
            ) : (
              <ul className="list-none p-0">
                {filteredHiddenVenues.map((venue) => {
                  return (
                    <VenueListItem
                      key={venue._id}
                      venue={venue}
                      editVenue={editVenue}
                      handleAddEvent={handleAddEvent}
                      actionLabel="Unhide"
                      actionHandler={unhideVenue}
                      unpublishVenue={unpublishVenue}
                      publishVenue={publishVenue}
                    />
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default VenueTab;
